import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { DeleteVersionDialogComponent } from '@components/delete-version-dialog/delete-version-dialog.component';
import { LeaveProjectDialogComponent } from '@components/leave-project-dialog/leave-project-dialog.component';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { ReleaseOwnershipDialogComponent } from '@components/release-ownership-dialog/release-ownership-dialog.component';
import { RemoveAccessDialogComponent } from '@components/remove-access-dialog/remove-access-dialog.component';
import { RequestAccessDialogComponent } from '@components/request-access-dialog/request-access-dialog.component';
import { RequestApprovalDialogComponent } from '@components/request-approval-dialog/request-approval-dialog.component';
import { StartVersionDialogComponent } from '@components/start-version-dialog/start-version-dialog.component';
import { AccessRequestSocketService } from '@service/access-request-socket.service';
import { AccessRequestService } from '@service/access-request.service';
import { AuthService } from '@service/auth.service';
import { UserPictureService } from '@service/user-picture.service';
import { VesselOwnershipService } from '@service/vessel-ownership.service';
import { VesselService } from '@service/vessel.service';
import { AccessRequest, AccessRequestStatus } from 'icreate/common/models/access-request';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import {
    OwnerChangePayload,
    VesselOwnershipStatus,
    VesselOwnershipTransfer,
} from 'icreate/common/models/vessel-ownership-transfer';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { OwnerChangeEvent } from '../vessel-page.component';
import { vesselOwnershipTransferCommentLength } from 'icreate/common/validators/vessel-ownership-transfer/vessel-ownership-transfer-validator';
import { Shipyard } from 'icreate/common/models/shipyard';
import { EnvironmentService } from '@service/environment.service';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';

@Component({
    selector: 'app-vessel-info-container',
    templateUrl: './vessel-info-container.component.html',
    styleUrls: ['./vessel-info-container.component.css'],
    standalone: false,
})
export class VesselInfoContainerComponent implements OnInit, OnChanges, OnDestroy {
    readonly REQUEST_COMMENT_MAX_LENGTH = vesselOwnershipTransferCommentLength;

    @Input() selectedVessel: Vessel;
    @Input() accessRequestId: string;
    @Input() shipyardiApi: string;
    @Input() approversList: { name: string; email: string; oid: string }[];
    @Input() ownershipTransfers: VesselOwnershipTransfer[];
    @Input() hasPreviousVersion: boolean;
    @Input() shipyardList: Shipyard[];
    @Output() onEditorLeave: EventEmitter<AccessRequest> = new EventEmitter<AccessRequest>();
    @Output() onOwnerChange: EventEmitter<OwnerChangeEvent> = new EventEmitter();
    @Output() onEditorsFound = new EventEmitter<any[]>();

    @ViewChild('ownershipReleaseConfirmationDialog')
    ownershipReleaseConfirmationDialog: ConfirmationWindowComponent;

    @ViewChild('ownershipRequestConfirmationDialog')
    ownershipRequestConfirmationDialog: ConfirmationWindowComponent;

    @ViewChild('claimOwnershipConfirmationDialog')
    claimOwnershipConfirmationDialog: ConfirmationWindowComponent;

    showDetails: boolean = true;

    accessRequested: boolean = false;
    isLoading: boolean = false;

    currentUser: { name: string; oid: string; email: string };
    ownerImage: string;
    vesselEditors: { image: string; name: string; oid: string; requestId: string }[] = [];

    destroySubscriptions$: Subject<void> = new Subject();

    requestComment: string | undefined = undefined;

    private env: RuntimeEnvironments;
    constructor(
        private authService: AuthService,
        private userPictureService: UserPictureService,
        private accessRequestService: AccessRequestService,
        private vesselService: VesselService,
        private accessRequestSocketService: AccessRequestSocketService,
        private dialog: MatDialog,
        private router: Router,
        private ownershipService: VesselOwnershipService,
        private environmentService: EnvironmentService
    ) {
        this.currentUser = this.authService.getCurrentUser();
        this.env = this.environmentService.getEnvironment();
    }

    async ngOnInit(): Promise<void> {
        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId === this.selectedVessel.id) {
                    const editorPicture = await this.userPictureService.getUserPicture(
                        accessRequest.userOid,
                        accessRequest.userName
                    );
                    this.vesselEditors.push({
                        image: editorPicture,
                        name: accessRequest.userName,
                        oid: accessRequest.userOid,
                        requestId: accessRequest.id,
                    });

                    const currentUserOid = this.authService.getCurrentUser().oid;
                    if (accessRequest.userOid == currentUserOid) {
                        this.accessRequestService.setCurrentAccessRequestId(accessRequest.id);
                    }
                    const updatedVessel = await this.vesselService.getVessel(this.selectedVessel.id);
                    await this.vesselService.updateVesselInList(updatedVessel);
                    this.selectedVessel = updatedVessel;
                    this.onEditorsFound.emit(this.vesselEditors);
                }
            });

        this.accessRequestSocketService
            .requestCreated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId === this.selectedVessel.id) {
                    if (accessRequest.userOid == this.currentUser.oid) this.accessRequested = true;
                }
            });

        this.accessRequestSocketService
            .finishedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId === this.selectedVessel.id) {
                    this.accessRequested = false;
                    let indexToDelete = this.vesselEditors.findIndex(editor => editor.oid === accessRequest.userOid);
                    if (indexToDelete !== -1) {
                        this.vesselEditors.splice(indexToDelete, 1);
                        this.onEditorLeave.emit(accessRequest);
                    }
                    this.onEditorsFound.emit(this.vesselEditors);
                }
            });
        this.accessRequestSocketService
            .requestUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async updatedRequest => {
                if (updatedRequest.vesselId === this.selectedVessel.id) {
                    await this.hasRequestedAccess();
                }
            });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedVessel && this.selectedVessel) {
            if (this.selectedVessel.ownerOid) {
                this.ownerImage = await this.userPictureService.getUserPicture(
                    this.selectedVessel.ownerOid,
                    this.selectedVessel.ownerName
                );
            }
            await this.checkAccessRequests();
            await this.hasRequestedAccess();
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    isGuestEditing(): boolean {
        return this.vesselEditors.length > 0;
    }

    isPendingApproval(): boolean {
        return this.selectedVessel.projectStatus === ProjectStatus.PendingApproval;
    }

    isEditingVessel(): boolean {
        return this.selectedVessel.projectStatus === ProjectStatus.Editing;
    }

    isApproved(): boolean {
        return (
            this.selectedVessel.projectStatus === ProjectStatus.Approved &&
            this.currentUser.oid === this.selectedVessel.ownerOid
        );
    }

    isOwnerVessel(): boolean {
        return this.currentUser.oid === this.selectedVessel.ownerOid;
    }

    shouldShowExtraButtons(): boolean {
        if (this.isOwnerVessel() && !this.isPendingApproval() && !this.isApproved()) {
            return true;
        }
        return !!this.accessRequestId;
    }

    openVersionsHistory() {
        this.router.navigateByUrl(`${this.selectedVessel.shipyardId}/${this.selectedVessel.id}/history`);
    }

    openWhyMessage() {
        this.dialog.open(PopUpNotificationComponent, {
            width: '474px',
            data: {
                title: 'vessel.page.notification.request-paused.title',
                message: 'vessel.page.notification.request-paused.message',
                message2: 'vessel.page.notification.request-paused.message-2',
            },
        });
    }

    private async checkAccessRequests(): Promise<void> {
        this.accessRequested = false;
        this.vesselEditors = [];

        if (!this.selectedVessel?.ownerOid) return;

        const requests = this.selectedVessel.VesselAccessRequests ?? [];
        if (requests.length === 0) return;

        const accessRequests = [];
        const accessRequestsIds = new Set();

        requests.forEach(accessRequest => {
            if (
                (accessRequest.requestStatus == AccessRequestStatus.Pending ||
                    accessRequest.requestStatus == AccessRequestStatus.Accepted ||
                    accessRequest.requestStatus == AccessRequestStatus.Editing) &&
                accessRequest.userOid == this.currentUser.oid &&
                !accessRequest.messageRead
            ) {
                this.accessRequested = true;
            }

            if (accessRequest.requestStatus === AccessRequestStatus.Editing) {
                if (!accessRequestsIds.has(accessRequest.id)) {
                    accessRequests.push(accessRequest);
                    accessRequestsIds.add(accessRequest.id);
                }
            }
        });

        if (accessRequests.length === 0) {
            this.onEditorsFound.emit([]);
            return;
        }

        const editorPromises = accessRequests.map(async accessRequest => {
            const editorPicture = await this.userPictureService.getUserPicture(
                accessRequest.userOid,
                accessRequest.userName
            );
            return {
                image: editorPicture,
                name: accessRequest.userName,
                oid: accessRequest.userOid,
                requestId: accessRequest.id,
            };
        });

        this.vesselEditors = await Promise.all(editorPromises);
    }

    async hasRequestedAccess() {
        const requests = await this.accessRequestService.getRequests(this.selectedVessel.id);
        if (requests.length === 0) this.accessRequested = false;
        else {
            this.accessRequested = requests.some(
                request =>
                    request.userOid === this.currentUser.oid &&
                    ((!request.messageRead && request.requestStatus === AccessRequestStatus.Pending) ||
                        request.requestStatus === AccessRequestStatus.Accepted ||
                        request.requestStatus === AccessRequestStatus.Editing)
            );
        }
    }

    async requestApproval() {
        if (this.isRunningInElectron) return;
        if (this.isGuestEditing()) {
            return;
        }
        const dialogRef = this.dialog.open(RequestApprovalDialogComponent, {
            width: '600px',
            data: { vessel: this.selectedVessel, shipyardiApi: this.shipyardiApi },
        });
        const requestSent = await lastValueFrom(dialogRef.afterClosed());
        if (requestSent) this.selectedVessel.projectStatus = ProjectStatus.PendingApproval;
    }

    async finishVersion() {
        try {
            if (!this.isRunningInElectron) return;
            await this.vesselService.finishVersion(this.selectedVessel.id, this.selectedVessel.projectVersion);
        } catch (err) {
            console.log(err);
        }
    }

    async requestVesselAccess() {
        const dialogRef = this.dialog.open(RequestAccessDialogComponent, { width: '474px' });
        const requestAccess = await lastValueFrom(dialogRef.afterClosed());
        if (requestAccess) {
            this.isLoading = true;
            try {
                await this.accessRequestService.requestAccess(
                    this.currentUser.name,
                    this.currentUser.oid,
                    this.selectedVessel.id,
                    this.selectedVessel.projectVersion
                );
                this.accessRequested = true;
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
        }
    }

    async leaveEditing(requestId) {
        if (!requestId) return;
        const dialogRef = this.dialog.open(LeaveProjectDialogComponent, { width: '474px' });
        const leaveProject = await lastValueFrom(dialogRef.afterClosed());
        if (leaveProject) {
            try {
                this.accessRequestId = null;
                this.accessRequestService.setCurrentAccessRequestId(undefined);
                this.accessRequested = false;
                await this.accessRequestService.leaveEdit(requestId);
            } catch (err) {
                console.error(err);
                this.accessRequestId = requestId;
                this.accessRequestService.setCurrentAccessRequestId(this.accessRequestId);
                this.accessRequested = true;
            }
        }
    }

    async deleteVersion() {
        if (this.isGuestEditing()) return;
        const dialogRef = this.dialog.open(DeleteVersionDialogComponent, {
            width: '474px',
            data: {
                hasPreviousVersion: this.hasPreviousVersion,
                vessel: this.selectedVessel,
                shipyardList: this.shipyardList,
            },
        });
        const shouldDelete = await lastValueFrom(dialogRef.afterClosed());
        if (shouldDelete) {
            let newVesselData;
            if (!this.hasPreviousVersion) {
                newVesselData = {
                    name: shouldDelete.name,
                    number: shouldDelete.number,
                    drawingId: shouldDelete.drawingId,
                    numberOps: shouldDelete.numberOps,
                    numberFcu: shouldDelete.numberFcu,
                };
            }

            this.isLoading = true;
            try {
                await this.vesselService.deleteVersion(
                    this.selectedVessel.id,
                    this.shipyardiApi,
                    this.selectedVessel.iAPIVesselId,
                    this.selectedVessel.projectVersion
                );
                await this.vesselService.update(newVesselData, this.selectedVessel);
                this.selectedVessel.projectStatus = ProjectStatus.undefined;
                this.selectedVessel.ownerName = null;
                this.selectedVessel.ownerOid = null;
                this.selectedVessel.locked = false;
                this.selectedVessel.name = newVesselData.name;
                this.selectedVessel.number = newVesselData.number;
                this.selectedVessel.drawingId = newVesselData.drawingId;
                this.selectedVessel.numberOps = newVesselData.numberOps;
                this.selectedVessel.numberFcu = newVesselData.numberFcu;
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
        }
    }

    async startNewVersion(inReferencePage = false) {
        const dialogRef = this.dialog.open(StartVersionDialogComponent, {
            width: '474px',
            data: { vessel: this.selectedVessel, shipyardIApi: this.shipyardiApi, inReferencePage },
        });
        const newVersionInfo = await lastValueFrom(dialogRef.afterClosed());
        if (newVersionInfo) {
            const isFirstVersion = !this.selectedVessel.projectVersion;
            this.isLoading = true;
            let referenceVersion =
                newVersionInfo.referenceVersion === this.selectedVessel.projectVersion
                    ? undefined
                    : newVersionInfo.referenceVersion;
            this.selectedVessel.ownerName = newVersionInfo.owner.name;
            this.selectedVessel.ownerOid = newVersionInfo.owner.oid;
            this.selectedVessel.projectVersion = newVersionInfo.versionName;
            this.selectedVessel.locked = true;
            this.selectedVessel.projectStatus = ProjectStatus.Editing;
            await this.vesselService.startVersion(
                this.selectedVessel,
                this.shipyardiApi,
                referenceVersion,
                isFirstVersion
            );
            this.isLoading = false;
        }
    }

    async removeUser(editor) {
        const dialogRef = this.dialog.open(RemoveAccessDialogComponent, { width: '474px' });
        const removeAccess = await lastValueFrom(dialogRef.afterClosed());
        if (removeAccess && editor) {
            this.isLoading = true;
            try {
                editor.email = this.getreceiverEmail(editor.oid);
                editor.vesselId = this.selectedVessel.id;
                editor.vesselName = this.selectedVessel.name;
                editor.shipyardId = this.selectedVessel.shipyardId;
                await this.accessRequestService.removeAccess(editor);
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
        }
    }

    async releaseOwnership() {
        if (this.isRunningInElectron) return;
        if (this.vesselEditors.length > 0) {
            this.dialog.open(PopUpNotificationComponent, {
                width: '474px',
                data: {
                    title: 'vessel.page.ownership.release.error.dialog.title',
                    message: 'vessel.page.ownership.release.error.dialog.message',
                    closeButton: 'vessel.page.ownership.release.error.dialog.close',
                },
            });
        } else {
            this.ownershipReleaseConfirmationDialog.setVisible(true);
            let confirmationOption = await lastValueFrom(
                this.ownershipReleaseConfirmationDialog.onOptionSelected.pipe(take(1))
            );
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                const dialogRef = this.dialog.open(ReleaseOwnershipDialogComponent, {
                    width: '600px',
                    data: { vessel: this.selectedVessel, shipyardiApi: this.shipyardiApi },
                });
                const ownershipData = await lastValueFrom(dialogRef.afterClosed());
                if (!ownershipData) return;

                this.onOwnerChange.emit(ownershipData);
            }
        }
    }

    async requestOwnership() {
        this.requestComment = '';
        this.ownershipRequestConfirmationDialog.setVisible(true);
        let confirmationOption = await lastValueFrom(
            this.ownershipRequestConfirmationDialog.onOptionSelected.pipe(take(1))
        );
        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            this.sendOwnershipRequestMail(this.requestComment);
        }
    }

    getOwnershipRequestPayload(comment?: string): OwnerChangePayload {
        return {
            shipyardId: this.selectedVessel.shipyardId,
            vesselId: this.selectedVessel.id,
            vesselName: this.selectedVessel?.number + ' - ' + this.selectedVessel?.name,
            senderName: this.currentUser.name,
            senderEmail: this.currentUser.email,
            senderOid: this.currentUser.oid,
            receiverName: this.selectedVessel.ownerName,
            receiverEmail: this.getreceiverEmail(this.selectedVessel.ownerOid),
            receiverOid: this.selectedVessel.ownerOid,
            comment: comment,
            vesselVersion: this.selectedVessel.projectVersion,
            skipGuard: true,
            ownershipStatus: VesselOwnershipStatus.Requested,
        };
    }

    async sendOwnershipRequestMail(comment?: string) {
        const ownershipPayload = this.getOwnershipRequestPayload(comment);

        try {
            this.isLoading = true;
            await this.ownershipService.createOwnershipRequest(ownershipPayload);
            this.dialog.open(PopUpNotificationComponent, {
                width: '474px',
                data: {
                    title: 'vessel.page.ownership.request.sent.dialog.title',
                    message: 'vessel.page.ownership.request.sent.dialog.message',
                    closeButton: 'vessel.page.ownership.request.sent.dialog.close',
                },
            });
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    getreceiverEmail(userOid: string) {
        const user = this.approversList.find(user => user.oid === userOid);
        return user?.email;
    }

    async claimOwnership() {
        this.claimOwnershipConfirmationDialog.setVisible(true);
        let confirmationOption = await lastValueFrom(
            this.claimOwnershipConfirmationDialog.onOptionSelected.pipe(take(1))
        );
        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            if (this.currentUser?.oid) {
                await this.ownershipService.claimVesselOwnership({
                    vesselId: this.selectedVessel.id,
                    vesselVersion: this.selectedVessel.projectVersion,
                    userName: this.currentUser.name ?? null,
                    userOid: this.currentUser.oid ?? null,
                });
            }
        }
    }

    newVersionButtonEnabled() {
        return this.selectedVessel.projectStatus === ProjectStatus.undefined;
    }

    isOwnershipClaimable() {
        return this.selectedVessel.projectStatus != ProjectStatus.undefined && !this.selectedVessel.ownerOid;
    }

    isOwnershipReleasable() {
        return this.selectedVessel.projectStatus != ProjectStatus.PendingApproval && this.isOwnerVessel();
    }

    isOwnershipRequestable() {
        return (
            this.selectedVessel.projectStatus != ProjectStatus.PendingApproval && !this.hasAlreadyRequestedOwnership()
        );
    }

    hasAlreadyRequestedOwnership(): boolean {
        return this.ownershipTransfers.some(
            transfer =>
                transfer.vesselId === this.selectedVessel.id &&
                transfer.userOid === this.currentUser.oid &&
                transfer.ownershipStatus === VesselOwnershipStatus.Requested &&
                !transfer.messageRead
        );
    }

    get isRunningInElectron() {
        return this.env == RuntimeEnvironments.ELECTRON;
    }
}
